import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import {computed} from "vue";
const useEmailSettings = () => {
  const queryClient = useQueryClient();

  const saveEmailFrequencyMutation = useMutation({
    mutationFn: (data) => axios.post('/ajax/v2/email-settings', data),
    onMutate: async (data) => {
      const queryKey = ['email-settings'];

      await queryClient.cancelQueries({queryKey});

      const previousSettings = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          email_frequency: data.email_frequency,
          weekly_day: data.weekly_day ?? null,
        };
      });

      successToast('Your email settings have been updated.');

      return {previousSettings};
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(['email-settings'], context.previousSettings);
      }

      errorToast('Something went wrong');
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['email-settings']});
    },
  });

  const settingsQuery = useQuery({
    queryKey: ['email-settings'],
    queryFn: async () => {
      const response = await axios.get('/ajax/v2/email-settings');

      return response.data.data;
    },
  });

  const weeklyDaySetting = computed(() => settingsQuery.data.value?.weekly_day ?? null);

  const agencyKitEmailRecipientsQuery = useQuery({
    queryKey: ['agency-kit-email-recipients'],
    queryFn: async () => {
      const response = await axios.get('/ajax/v2/email-settings/seo-agent-email-recipients');

      return response.data.data;
    },
  });

  const saveAgencyKitEmailRecipientsMutation = useMutation({
    mutationFn: async (data) => {
      if (data.recipient_ids.length === 0) {
        errorToast('Please select at least one recipient.');

        throw new Error('At least one recipient is required');
      }

      return axios.post('/ajax/v2/email-settings/seo-agent-email-recipients', data);
    },
    onMutate: async (data) => {
      const queryKey = ['agency-kit-email-recipients'];

      const previousRecipients = queryClient.getQueryData(queryKey);

      await queryClient.cancelQueries({queryKey});

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          selected_recipients: data.recipient_ids,
        };
      });

      if (data.recipient_ids.length !== 0) {
        successToast('Email recipients have been updated.');
      }

      return {previousRecipients};
    },
    onError: (_error, _variables, context) => {
      if (context?.previousRecipients) {
        queryClient.setQueryData(['agency-kit-email-recipients'], context.previousRecipients);
      }

      if (error.message === 'At least one recipient is required') {
        errorToast(error.message);
        return;
      }

      errorToast('Something went wrong');
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['agency-kit-email-recipients']});
    },
  });

  return {
    saveEmailFrequencyMutation,
    settingsQuery,
    weeklyDaySetting,
    agencyKitEmailRecipientsQuery,
    saveAgencyKitEmailRecipientsMutation,
  };
};

export default useEmailSettings;
